/**
 * 主题管理 Store
 * 支持浅色和深色主题切换
 * 区分 app / dmg / ytb 路由的主题，互不影响
 */
import { defineStore } from "pinia";
import { isDmgRoutePath, isYtbRoutePath } from "@/router/lib/route-prefix.js";

export const useThemeStore = defineStore("theme", {
    state: () => ({
        theme: {
            app: 'light',   // 主站路由的主题 'light' | 'dark'
            dmg: 'dark',    // DMG 固定暗色（无开关）
            ytb: 'dark',    // Ytb 固定暗色（无开关）
        },
        routeType: 'app',   // 'app' | 'dmg' | 'ytb'，当前路由类型
    }),

    getters: {
        /**
         * 获取当前路由类型的主题
         * @returns {string} 'light' | 'dark'
         */
        currentTheme: (state) => {
            return state.theme[state.routeType];
        },

        /**
         * 是否为暗色主题
         * @returns {boolean}
         */
        isDark() {
            return this.currentTheme === 'dark';
        },
    },

    actions: {
        /**
         * 应用主题到 DOM
         * @param {string} theme - 'light' | 'dark'
         */
        _applyTheme(theme) {
            const html = document.documentElement
            html.classList.toggle('dark', theme === 'dark');
        },

        /** 同步布局标识到 html，供 dmg-dark-theme.css / ytb-theme.css 限定覆盖 */
        _applyRouteTypeClass(routeType) {
            const html = document.documentElement;
            html.classList.toggle('dmg-route', routeType === 'dmg');
            html.classList.toggle('ytb-route', routeType === 'ytb');
        },

        /**
         * 获取指定路由类型的主题
         * @param {string} routeType - 'app' | 'dmg' | 'ytb'
         * @returns {string} 'light' | 'dark'
         */
        _getThemeByRouteType(routeType) {
            // DMG / YTB 无主题开关，始终暗色
            if (routeType === 'dmg' || routeType === 'ytb') return 'dark';
            return this.theme[routeType];
        },

        /**
         * 设置指定路由类型的主题
         * @param {string} routeType - 'app' | 'dmg' | 'ytb'
         * @param {string} theme - 'light' | 'dark'
         */
        _setThemeByRouteType(routeType, theme) {
            if (routeType === 'dmg' || routeType === 'ytb') return;
            this.theme[routeType] = theme;
        },

        /**
         * 设置当前路由类型的主题
         * @param {string} theme - 'light' | 'dark'
         */
        setTheme(theme) {
            this._setThemeByRouteType(this.routeType, theme);
            this._applyTheme(this._getThemeByRouteType(this.routeType));
        },

        /**
         * 设置路由类型并应用对应主题
         * @param {string} routeType - 'app' | 'dmg' | 'ytb'
         */
        setRouteType(routeType) {
            this.routeType = routeType;
            this._applyRouteTypeClass(routeType);
            this._applyTheme(this._getThemeByRouteType(routeType));
        },

        /**
         * 根据路径自动设置路由类型并应用对应主题
         * @param {string} path - 路由路径
         */
        setRouteTypeByPath(path) {
            const routeType = isDmgRoutePath(path) ? 'dmg'
                : isYtbRoutePath(path) ? 'ytb'
                : 'app';
            this.setRouteType(routeType);
        },

        /**
         * 初始化主题 ( 在应用启动时调用 )
         */
        init() {
            const path = typeof window !== 'undefined' ? window.location.pathname : '/';
            const routeType = isDmgRoutePath(path) ? 'dmg'
                : isYtbRoutePath(path) ? 'ytb'
                : 'app';
            this.routeType = routeType;
            this._applyRouteTypeClass(routeType);
            this._applyTheme(this._getThemeByRouteType(routeType));
        },
    },

    persist: {
        key: 'app-theme',
        storage: localStorage,
        paths: ['theme', 'routeType'],
    },
});

